import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAppTheme } from '@/context/ThemeContext';

const ReportIssue: React.FC = () => {
  const { themeConfig } = useAppTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    issueType: 'App Issue',
    description: '',
    attachFile: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const issueTypes = [
    'App Issue',
    'Payment Problem',
    'Account Issue',
    'Product Management',
    'Order Processing',
    'Data Discrepancy',
    'Other'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      issueType: 'App Issue',
      description: '',
      attachFile: null
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  return (
    <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: themeConfig.primary }}>
            Report an Issue
          </h1>
          <p className="text-gray-600 mt-2">
            We're sorry you're experiencing an issue. Please fill out the form below and we'll address it as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Report Form */}
          <div 
            className="p-6 rounded-lg"
            style={{ backgroundColor: themeConfig.cardBackground }}
          >
            {submitSuccess ? (
              <div 
                className="p-4 rounded-lg mb-4"
                style={{ backgroundColor: '#d1fae5' }}
              >
                <p className="text-green-800 font-medium">
                  Your issue has been reported successfully! Our team will investigate and get back to you soon.
                </p>
              </div>
            ) : null}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label 
                    htmlFor="name" 
                    className="block text-sm font-medium mb-1"
                    style={{ color: themeConfig.text }}
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ 
                      borderColor: themeConfig.accent,
                      backgroundColor: themeConfig.background
                    }}
                  />
                </div>
                
                <div>
                  <label 
                    htmlFor="email" 
                    className="block text-sm font-medium mb-1"
                    style={{ color: themeConfig.text }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ 
                      borderColor: themeConfig.accent,
                      backgroundColor: themeConfig.background
                    }}
                  />
                </div>
                
                <div>
                  <label 
                    htmlFor="issueType" 
                    className="block text-sm font-medium mb-1"
                    style={{ color: themeConfig.text }}
                  >
                    Issue Type
                  </label>
                  <select
                    id="issueType"
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ 
                      borderColor: themeConfig.accent,
                      backgroundColor: themeConfig.background
                    }}
                  >
                    {issueTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label 
                    htmlFor="description" 
                    className="block text-sm font-medium mb-1"
                    style={{ color: themeConfig.text }}
                  >
                    Issue Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Please describe the issue in detail. Include any error messages, steps to reproduce, and when it started occurring."
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ 
                      borderColor: themeConfig.accent,
                      backgroundColor: themeConfig.background
                    }}
                  />
                </div>
                
                <div>
                  <label 
                    htmlFor="attachFile" 
                    className="block text-sm font-medium mb-1"
                    style={{ color: themeConfig.text }}
                  >
                    Attach Screenshot (optional)
                  </label>
                  <input
                    type="file"
                    id="attachFile"
                    name="attachFile"
                    accept="image/*"
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ 
                      borderColor: themeConfig.accent,
                      backgroundColor: themeConfig.background
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max file size: 5MB. Accepted formats: JPG, PNG, GIF
                  </p>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-2 px-4 rounded-md font-medium"
                    style={{ 
                      backgroundColor: themeConfig.primary,
                      color: 'white',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* FAQ Reference */}
          <div 
            className="p-4 rounded-lg flex items-start"
            style={{ backgroundColor: themeConfig.accent }}
          >
            <QuestionMarkCircleIcon 
              className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" 
              style={{ color: themeConfig.primary }} 
            />
            <div>
              <p className="text-sm" style={{ color: themeConfig.text }}>
                Before submitting a report, check if your issue is addressed in our FAQ section.
              </p>
              <Link 
                to="/help/faq" 
                className="text-sm font-medium mt-1 inline-block"
                style={{ color: themeConfig.primary }}
              >
                View Frequently Asked Questions
              </Link>
            </div>
          </div>
        </div>
      </div>
  );
}

export default ReportIssue;
